import React from "react";
import { createPortal } from "react-dom";
import GrainDialogHost from "../GrainDialogHost.jsx";
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  PauseIcon,
  XCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { serializeDispatchAlertCaptureContext } from "../../lib/dispatchAlertCaptureContext.js";
import { renderInlineNoBreakIdentifiers } from "../../utils/inlineMessageFormatting.js";
import { TOAST_PROGRESS_LOADING_PANEL_CLASS } from "../../utils/toastProgress.js";
import { getReactPortalRoot } from "../../utils/reactPortalRoot.js";
import { joinClasses } from "./shared.js";
import ToastOperationProgress from "./ToastOperationProgress.jsx";

const ToastContext = React.createContext(null);

const TOAST_DEFAULTS = {
  paused: {
    title: "Confirmation requise",
    message: "Le traitement attend votre confirmation.",
    duration: null,
  },
  loading: {
    title: "Calcul en cours…",
    message: "Vous pouvez naviguer, vous serez notifié à la fin.",
    duration: null,
  },
  success: {
    title: "Opération terminée",
    message: "Le traitement s'est terminé avec succès.",
    duration: 5000,
  },
  error: {
    title: "Erreur",
    message: "Une erreur est survenue pendant le traitement.",
    duration: 8000,
  },
  info: {
    title: "Information",
    message: "Une mise à jour est disponible.",
    duration: 5000,
  },
  warning: {
    title: "À vérifier",
    message: "",
    duration: 8000,
  },
};

const TOAST_STYLE_MAP = {
  paused: {
    accentClassName: "border-l-amber-500",
    icon: PauseIcon,
    iconClassName: "text-amber-700",
    iconShellClassName: "border border-amber-200 bg-amber-50",
    panelClassName: "max-h-[50dvh] !overflow-y-auto",
  },
  loading: {
    accentClassName: "border-l-[#cbd5e1]",
    progressClassName: "bg-[#cbd5e1]",
    icon: ArrowPathIcon,
    iconClassName: "toast-loading-spinner h-6 w-6 motion-safe:animate-spin motion-reduce:animate-none",
    iconShellClassName: "h-13 w-13 rounded-[4px] bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(242,247,245,0.98))] shadow-[inset_0_1px_0_rgba(255,255,255,0.94),0_14px_28px_rgba(15,23,42,0.09)]",
    panelClassName: `${TOAST_PROGRESS_LOADING_PANEL_CLASS} rounded-[4px] border-[rgba(15,23,42,0.12)] bg-[linear-gradient(180deg,rgba(255,255,255,0.99),rgba(244,248,247,0.98))] shadow-[0_28px_64px_rgba(15,23,42,0.2),0_10px_24px_rgba(15,23,42,0.1)]`,
    titleClassName: "text-[16px] font-bold leading-6",
    messageClassName: "text-[14px] leading-5 text-[#526071]",
  },
  success: {
    accentClassName: "border-l-[#3a7d44]",
    progressClassName: "bg-[#3a7d44]",
    icon: CheckCircleIcon,
    iconClassName: "text-[#3a7d44]",
  },
  error: {
    accentClassName: "border-l-[#dc2626]",
    progressClassName: "bg-[#dc2626]",
    icon: XCircleIcon,
    iconClassName: "text-[#dc2626]",
  },
  info: {
    accentClassName: "border-l-[#185FA5]",
    progressClassName: "bg-[#185FA5]",
    icon: InformationCircleIcon,
    iconClassName: "text-[#185FA5]",
  },
  warning: {
    accentClassName: "border-l-[#a86a24]",
    progressClassName: "bg-[#a86a24]",
    icon: ExclamationTriangleIcon,
    iconClassName: "text-[#a86a24]",
  },
};

function createToastId() {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }

  return `toast-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function resolveToast(type, toast, previousToast = null) {
  const defaults = TOAST_DEFAULTS[type] || TOAST_DEFAULTS.info;
  const hasExplicitDuration = Object.prototype.hasOwnProperty.call(toast, "duration");
  const shouldResetDuration = !previousToast || previousToast.type !== type;

  return {
    ...previousToast,
    ...toast,
    type,
    progress: ["loading", "paused"].includes(type) || (toast.operationPending ?? previousToast?.operationPending)
      ? toast.progress ?? previousToast?.progress ?? null : null,
    title: toast.title ?? previousToast?.title ?? defaults.title,
    message: toast.message ?? previousToast?.message ?? defaults.message,
    duration: hasExplicitDuration
      ? toast.duration ?? null
      : shouldResetDuration
        ? defaults.duration
        : previousToast?.duration ?? defaults.duration,
  };
}

function ToastProgressBar({ duration, progressClassName, progressKey }) {
  const [isRunning, setIsRunning] = React.useState(false);

  React.useEffect(() => {
    if (!duration) {
      return undefined;
    }

    setIsRunning(false);

    const frameId = window.requestAnimationFrame(() => {
      setIsRunning(true);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [duration, progressKey]);

  if (!duration) {
    return null;
  }

  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-0.75 overflow-hidden rounded-b-[4px] bg-transparent">
      <div
        className={joinClasses([
          "h-full origin-left rounded-b-[4px]",
          progressClassName,
        ])}
        style={{
          transform: isRunning ? "scaleX(0)" : "scaleX(1)",
          transitionDuration: `${duration}ms`,
          transitionProperty: "transform",
          transitionTimingFunction: "linear",
        }}
      />
    </div>
  );
}

function ToastItem({ toast, onDismiss }) {
  const styleMeta = TOAST_STYLE_MAP[toast.type] || TOAST_STYLE_MAP.info;
  const Icon = styleMeta.icon;
  const isPersistentToast = ["loading", "paused"].includes(toast.type) || toast.operationPending === true;
  const isHighEmphasis = toast.emphasis === "high" && !isPersistentToast;
  const hasCustomPosition = Boolean(toast.positionStyle && typeof toast.positionStyle === "object");
  const isErrorToast = toast.type === "error";
  const emphasisPanelClassName = isHighEmphasis
    ? (toast.type === "error"
      ? "w-[min(548px,calc(100vw-20px))] border-[rgba(220,38,38,0.18)] border-l-[5px] shadow-[0_24px_56px_rgba(220,38,38,0.22),0_8px_20px_rgba(15,23,42,0.12)]"
      : "w-[min(548px,calc(100vw-20px))] border-[rgba(15,23,42,0.12)] border-l-[5px] shadow-[0_24px_56px_rgba(15,23,42,0.18),0_8px_20px_rgba(15,23,42,0.12)]")
    : "";

  React.useEffect(() => {
    if (!toast.duration) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      onDismiss(toast.id);
    }, toast.duration);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [onDismiss, toast.duration, toast.id, toast.version]);

  return (
    <div
      data-toast-type={toast.type}
      tabIndex={toast.type === "paused" ? 0 : undefined}
      className={joinClasses([
        "pointer-events-auto relative w-[320px] max-w-full overflow-hidden rounded-[4px] border border-[rgba(15,23,42,0.08)] border-l-[3px] bg-white shadow-[0_18px_42px_rgba(15,23,42,0.14),0_3px_10px_rgba(15,23,42,0.08)]",
        emphasisPanelClassName,
        styleMeta.accentClassName,
        styleMeta.panelClassName,
        toast.panelClassName,
      ])}
      data-dispatch-alert-capture-type={isErrorToast ? "global_error" : undefined}
      data-dispatch-alert-capture-occurrence={isErrorToast ? `${toast.id}:${toast.version}` : undefined}
      data-dispatch-alert-capture-title={isErrorToast ? toast.title : undefined}
      data-dispatch-alert-capture-message={isErrorToast ? toast.message : undefined}
      data-dispatch-alert-capture-context={isErrorToast
        ? serializeDispatchAlertCaptureContext({
          ...(toast.captureContext && typeof toast.captureContext === "object" && !Array.isArray(toast.captureContext)
            ? toast.captureContext
            : {}),
          surface: "toast",
        }, {
          source: "notification",
        }) || undefined
        : undefined}
      style={hasCustomPosition ? { position: "fixed", zIndex: 10002, ...toast.positionStyle } : undefined}
    >
      <div aria-atomic="true" aria-live={isErrorToast ? "assertive" : "polite"} role={isErrorToast ? "alert" : "status"} className={joinClasses([
        "flex items-start gap-3 px-4 py-3.5",
        isPersistentToast ? "gap-3 px-5 py-3" : "",
        isHighEmphasis ? "gap-3.5 px-4.5 py-4" : "",
      ])}>
        <div className={joinClasses([
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-[4px] bg-[rgba(248,250,252,0.9)]",
          isHighEmphasis ? "h-10 w-10 shadow-[0_12px_28px_rgba(15,23,42,0.12)]" : "",
          styleMeta.iconShellClassName,
        ])}>
          <Icon className={joinClasses([isHighEmphasis ? "h-[1.1rem] w-[1.1rem]" : "h-4 w-4", styleMeta.iconClassName])} />
        </div>
        <div className="min-w-0 flex-1 pr-1">
          <div className={joinClasses([
            "text-[13px] font-semibold leading-5 text-floating-ink-strong",
            isHighEmphasis ? "text-[14px] font-bold leading-5" : "",
            styleMeta.titleClassName,
          ])}>
            {renderInlineNoBreakIdentifiers(toast.title)}
          </div>
          <div className={joinClasses([
            "text-[12px] leading-5 text-floating-muted",
            toast.captureContext?.action === "gantt_source_backed_save" ? "whitespace-pre-wrap [overflow-wrap:anywhere]" : "",
            isPersistentToast ? "mt-0" : "mt-0.5",
            isHighEmphasis ? "mt-1 text-[12.5px] leading-[1.35rem] text-[#425061]" : "",
            styleMeta.messageClassName,
          ])}>
            {renderInlineNoBreakIdentifiers(toast.message)}
          </div>
			{toast.actionLabel && typeof toast.onAction === "function" ? (
				<button
					type="button"
					className="mt-2 text-[12px] font-bold text-[var(--color-brand)] underline decoration-transparent underline-offset-2 transition hover:decoration-current"
					onClick={() => {
						if (toast.dismissOnAction !== false) {
							onDismiss(toast.id);
						}
						toast.onAction();
					}}
				>
					{toast.actionLabel}
				</button>
			) : null}
        </div>
        {!isPersistentToast ? (
          <button
            type="button"
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-[4px] border border-transparent text-floating-faint transition-colors duration-150 hover:border-[rgba(15,23,42,0.08)] hover:bg-[rgba(248,250,252,0.92)] hover:text-floating-copy"
            aria-label="Fermer la notification"
            onClick={() => onDismiss(toast.id)}
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        ) : null}
      </div>
      {isPersistentToast && toast.progress?.steps?.length > 0 ? (
        <ToastOperationProgress progress={toast.progress} />
      ) : null}
      {!isPersistentToast ? (
        <ToastProgressBar
          duration={toast.duration}
          progressClassName={styleMeta.progressClassName}
          progressKey={`${toast.id}:${toast.version}`}
        />
      ) : null}
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = React.useState([]);

  const dismissToast = React.useCallback((id) => {
    setToasts((currentToasts) =>
      currentToasts.filter((toast) => toast.id !== id),
    );
  }, []);

  const showToast = React.useCallback((toast) => {
    const type = toast?.type || "info";
    const id = createToastId();

    setToasts((currentToasts) => [
      ...currentToasts,
      {
        id,
        version: 0,
        ...resolveToast(type, toast || {}),
      },
    ]);

    return id;
  }, []);

  const updateToast = React.useCallback((id, updates) => {
    setToasts((currentToasts) =>
      currentToasts.map((toast) => {
        if (toast.id !== id) {
          return toast;
        }

        const nextType = updates?.type || toast.type;

        return {
          ...resolveToast(nextType, updates || {}, toast),
          id,
          version: toast.version + 1,
        };
      }),
    );
  }, []);

  const toastContextValue = React.useMemo(
    () => ({
      dismissToast,
      showToast,
      updateToast,
    }),
    [dismissToast, showToast, updateToast],
  );
  const toastMountNode = getReactPortalRoot();
  const toastStack = (
    <div
      className="pointer-events-none fixed bottom-5 right-5 flex max-w-[calc(100%-2.5rem)] flex-col gap-3"
      data-react-toast-stack="true"
      style={{ zIndex: "calc(var(--z-index-modal) + 20)" }}
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={dismissToast} />
      ))}
    </div>
  );

  return (
    <ToastContext.Provider value={toastContextValue}>
      {children}
      <GrainDialogHost />
      {toastMountNode instanceof HTMLElement ? createPortal(toastStack, toastMountNode) : toastStack}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = React.useContext(ToastContext);

  if (!context) {
    throw new Error("useToast must be used within a ToastProvider.");
  }

  return context;
}
